export function mapThruthy<T, U>(arr: T[], mapper: (item: T) => U | undefined): U[] {
  return arr.reduce<U[]>((acc, item) => {
    const mapped = mapper(item);

    if (mapped) {
      acc.push(mapped);
    }

    return acc;
  }, []);
}

export function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
}

export function warn(message: string, ...args: unknown[]) {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(message, ...args);
  }
}

export function invariant(condition: boolean, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

export function isUrl(str: string) {
  return str.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g);
}

/**
 * Take an array of objects and return a new array with the nth occurance of the same object removed.
 * @param iteratee
 * @param property
 * @param nthOccurance - optional, default is 2
 */
export function removeNthOccurances<T>(iteratee: T[], property: string, nthOccurance: number = 2) {
  const tally = {};
  const results: T[] = [];

  iteratee.forEach((item) => {
    if (!tally[item[property]]) {
      tally[item[property]] = 0;
    }

    if (tally[item[property]] < nthOccurance) {
      tally[item[property]] += 1;
      results.push(item);
    }
  });

  return results;
}

export function classes(...args: (string | boolean | undefined)[]) {
  return args.filter(Boolean).join(' ');
}

export function mod(n: number, l: number) {
  return ((n % l) + l) % l;
}

export function normalizeString(str: string) {
  if (!str) {
    return '';
  }
  return str.toLocaleLowerCase().replace(/\s/g, '');
}

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export function toMap<T>(arr: T[] | Set<T>, selector: (item: T) => string): Map<string, T> {
  return new Map(Array.from(arr).map((el) => [selector(el), el]));
}

export function isSameInArray<T>(arr: T[], item: T, property: keyof T) {
  return Boolean(arr.find((arrayItem) => arrayItem[property] === item[property]));
}

export function hasRequiredFields<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): obj is T & Required<Pick<T, K>> {
  const missingKeys = new Set<K>();

  for (const key of keys) {
    if (obj[key] === undefined) {
      missingKeys.add(key);
    }
  }

  if (missingKeys.size > 0) {
    console.warn(`Missing required fields: ${Array.from(missingKeys).join(', ')}`);
    return false;
  }

  return true;
}

export function assertNonNullable<T>(value: T, label?: string): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error(`${label || 'Value'} is ${value}`);
  }
}

export function assertRequiredFields<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): asserts obj is T & Required<Pick<T, K>> {
  if (!hasRequiredFields(obj, keys)) {
    const missingKeys = new Set<K>();

    for (const key of keys) {
      if (obj[key] === undefined) {
        missingKeys.add(key);
      }
    }

    throw new Error(`Missing required fields: ${Array.from(missingKeys).join(', ')}`);
  }
}

export function getPercent(value: number, total: number) {
  return (value * 100) / total;
}

export class URLPath {
  public search: URLSearchParams;
  public segments: string[];

  constructor(...segments: string[]) {
    this.search = new URLSearchParams();
    this.segments = segments;
  }

  toString() {
    return `/${this.segments.join('/')}?${this.search.toString()}`;
  }
}

export const lowercase = <T extends string>(str: T) => str.toLowerCase() as Lowercase<T>;

interface ComposeEventHandlersOptions {
  checkForDefaultPrevented?: boolean;
}

/**
 * Composes two event handlers into one. The original event handler will be called first, then the ourEventHandler.
 * @param originalEventHandler
 * @param ourEventHandler
 * @param options.checkForDefaultPrevented if true, the event handler will not be called if the event's defaultPrevented property is true
 * @returns
 */
export function composeEventHandlers<E>(
  originalEventHandler?: (event: E) => void,
  ourEventHandler?: (event: E) => void,
  { checkForDefaultPrevented = true }: ComposeEventHandlersOptions = {},
) {
  return function handleEvent(event: E) {
    originalEventHandler?.(event);

    if (checkForDefaultPrevented === false || !(event as unknown as Event).defaultPrevented) {
      return ourEventHandler?.(event);
    }
  };
}
